import React, { Component, createRef } from "react";
import {
  Button,
  Row,
  Col,
  FormGroup,
  FormFeedback,
  Label,
  Input,
} from "reactstrap";
import NumberFormat from "react-number-format";
import { Formik } from "formik";
import { get } from "lodash";
import PropTypes from "prop-types";
import usersApi from "@/api/users";
import accountsApi from "@/api/accounts";
import {
  getTransferSchema,
  isAccountId,
  clearPhoneNumber,
} from "@/util/validationHelper";
import { handleError } from "@/util/errorHandler";
import InputMask from "react-input-mask";

export default class TransferForm extends Component {
  state = {
    selectedCurrency: this.props.initialValues.currency,
    isCheckingAccountExistence: false,
    accountName: null,
    name: null,
  };

  getValidationSchema() {
    const { balances } = this.props;
    const { selectedCurrency } = this.state;
    const balance = balances[selectedCurrency];

    return getTransferSchema({ selectedCurrency, balance });
  }

  handleCurrencyChange({ target: { value: selectedCurrency } }) {
    this.setState({ selectedCurrency });
  }

  onSubmit = async () => {
    const values = get(this.formik, "current.state.values", {});

    if (values.fromName) {
      if (!values.fromPhone) {
        this.formik.current &&
          this.formik.current.setFieldError("fromPhone", "Enter phone number");

        return;
      }
    }

    let isValid = Boolean(this.state.accountName);

    if (!isValid) {
      isValid = await this.checkAccountExistence();
    }

    if (isValid) {
      this.props.onSubmit({
        ...values,
        accountName: this.state.accountName,
        accountId: this.state.accountId,
      });
    }
  };

  checkAccountExistence = async () => {
    try {
      this.setState({ isCheckingAccountExistence: true });
      let transferTo = get(this.formik, "current.state.values.transferTo");

      let selectedApi = null;
      if (isAccountId(transferTo)) {
        selectedApi = usersApi.getUserInfoByAccountName;
        transferTo = transferTo.toLowerCase();
      } else {
        selectedApi = usersApi.getUserInfoByPhoneNumber;
        const isValidEmail = /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          transferTo,
        );
        if (!isValidEmail) {
          transferTo = clearPhoneNumber(transferTo);
        }
      }
      const { accountId, accountName, name } = await selectedApi(transferTo);
      const {
        frozen,
        accountStatus,
      } = await accountsApi.getIrohaAccountDetails(accountId);

      if (frozen) {
        throw new Error(`Account ${accountName} is blocked`);
      }

      if (accountStatus === "DEACTIVATED") {
        throw new Error(`Account ${accountName} is deactivated`);
      }

      if (accountStatus === "DELETED") {
        throw new Error(`Account ${accountName} is deleted`);
      }

      this.setState({ accountName, name, accountId });

      return true;
    } catch (err) {
      const status = get(err, "response.status");

      if (status === 500) {
        handleError(err);
        return false;
      }

      const errorMessage = get(
        err,
        "response.data.status.message",
        err.message,
      );
      this.formik.current &&
        this.formik.current.setErrors({ transferTo: errorMessage });

      return false;
    } finally {
      this.setState({ isCheckingAccountExistence: false });
    }
  };

  formik = createRef();

  render() {
    const { initialValues } = this.props;
    const { accountName, isCheckingAccountExistence } = this.state;

    return (
      <Formik
        ref={this.formik}
        initialValues={initialValues}
        validationSchema={this.getValidationSchema()}
        onSubmit={this.onSubmit}>
        {({
          values,
          handleSubmit,
          handleBlur,
          handleChange,
          handleReset,
          errors,
          touched,
        }) => (
          <Row>
            <Col xl={6} sm={12}>
              <FormGroup>
                <Label>Transfer to:</Label>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "100%", marginRight: 20 }}>
                    <Input
                      type="text"
                      name="transferTo"
                      placeholder="Enter account ID or primary contact"
                      value={values.transferTo}
                      onChange={event => {
                        this.setState({ accountName: null });
                        handleChange(event);
                      }}
                      onBlur={handleBlur}
                      invalid={Boolean(errors.transferTo && touched.transferTo)}
                      data-cy="transfer-to"
                    />
                    <FormFeedback>{errors.transferTo}</FormFeedback>
                  </div>
                  <Button
                    style={{ minWidth: 130, height: 35 }}
                    onClick={this.checkAccountExistence}
                    disabled={Boolean(
                      !values.transferTo ||
                        errors.transferTo ||
                        isCheckingAccountExistence,
                    )}
                    data-cy="account-check">
                    Account check
                  </Button>
                </div>
                {accountName && (
                  <div style={{ marginTop: 10 }} data-cy="account-name">
                    Name: {accountName}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Transfer from (phone):</Label>
                <InputMask
                  name="fromPhone"
                  mask="+855 99 999 9999"
                  maskChar=""
                  value={values.fromPhone}
                  onChange={handleChange}>
                  {inputProps => (
                    <Input
                      {...inputProps}
                      placeholder="Enter phone number"
                      type="text"
                      invalid={Boolean(errors.fromPhone)}
                    />
                  )}
                </InputMask>
                <FormFeedback>{errors.fromPhone}</FormFeedback>
              </FormGroup>
              <FormGroup>
                <Label>Transfer from (name):</Label>
                <Input
                  type="text"
                  name="fromName"
                  placeholder="Enter name"
                  value={values.fromName}
                  onChange={event => {
                    handleChange(event);
                  }}
                  onBlur={handleBlur}
                  invalid={Boolean(errors.fromName && touched.fromName)}
                  data-cy="description"
                />
                <FormFeedback>{errors.fromName}</FormFeedback>
              </FormGroup>
              <FormGroup>
                <Label>Choose currency:</Label>
                <Input
                  type="select"
                  name="currency"
                  id="currency"
                  value={values.currency}
                  onChange={event => {
                    this.handleCurrencyChange(event);
                    handleChange(event);
                  }}
                  onBlur={handleBlur}
                  data-cy="currency">
                  <option value="KHR">KHR</option>
                  <option value="USD">USD</option>
                  <option value="XAU">GOLD</option>
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Amount:</Label>
                <NumberFormat
                  name="amount"
                  className={`form-control ${
                    errors.amount && touched.amount ? "is-invalid" : ""
                  }`}
                  placeholder="Enter amount"
                  value={values.amount}
                  decimalScale={values.currency === "KHR" ? 0 : 2}
                  decimalSeparator="."
                  thousandSeparator=" "
                  fixedDecimalScale={values.currency === "KHR" ? false : true}
                  allowNegative={false}
                  allowLeadingZeros={false}
                  displayType="input"
                  onValueChange={value => {
                    const event = {
                      target: {
                        value: value.value,
                        name: "amount",
                      },
                    };

                    handleChange(event);
                  }}
                  onBlur={handleBlur}
                  invalid={String(errors.amount && touched.amount)}
                  data-cy="amount"
                />
                <FormFeedback>{errors.amount}</FormFeedback>
              </FormGroup>
              <FormGroup>
                <Label>External reference (ID):</Label>
                <Input
                  type="text"
                  name="extRef"
                  placeholder="Enter external reference"
                  value={values.extRef}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={Boolean(errors.extRef && touched.extRef)}
                  data-cy="description"
                />
                <FormFeedback>{errors.extRef}</FormFeedback>
              </FormGroup>
              <FormGroup>
                <Label>Description:</Label>
                <Input
                  type="text"
                  name="description"
                  placeholder="Enter description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={Boolean(errors.description && touched.description)}
                  data-cy="description"
                />
                <FormFeedback>{errors.description}</FormFeedback>
              </FormGroup>
              <div style={{ textAlign: "right" }}>
                <Button
                  style={{ width: 100 }}
                  onClick={handleReset}
                  disabled={isCheckingAccountExistence}
                  data-cy="clear">
                  Clear
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  style={{ marginLeft: 20, width: 100 }}
                  onClick={handleSubmit}
                  disabled={isCheckingAccountExistence}
                  data-cy="next">
                  Send
                </Button>
              </div>
            </Col>
          </Row>
        )}
      </Formik>
    );
  }
}

TransferForm.defaultProps = {
  onSubmit: () => {},
  initialValues: {},
  balances: { USD: 0, KHR: 0, XAU: 0 },
};

TransferForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  balances: PropTypes.shape({
    USD: PropTypes.number.isRequired,
    KHR: PropTypes.number.isRequired,
    XAU: PropTypes.number.isRequired,
  }),
};
